import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { INSURANCE_CONVENTION_INSURERS, INSURERS } from '../../../queries';
import { ADD_UPDATE_INSURANCE_CONVENTION_INSURER, DELETE_CONVENTION_INSURER } from '../../../mutations';
import { INSURANCE_CONVENTION_INSURER_UPDATED, CONVENTION_INSURER_DELETED } from '../../../subscriptions';
import InsuranceConventionInsurerComponent from './InsuranceConventionInsurerComponent';
import { useChangeTrackingLens, set, get } from '@totalsoft/change-tracking-react';
import { useApolloClient } from '@apollo/client';
import { useMutation, useSubscription, useQuery } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { sortBy } from 'utils/functions';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { insuranceConventionInsurerValidator } from 'features/insuranceConventions/edit/validators';
import { isValid } from '@totalsoft/pure-validations-react';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const InsuranceConventionInsurerContainer = ({ productId }) => {
  const [insurersLens, dirtyInfo, resetInsurersLens] = useChangeTrackingLens();
  const addToast = useToast();
  const confirm = useConfirmDialog();
  const client = useApolloClient();
  const { t } = useTranslation();
  const [validation, validate] = useDirtyFieldValidation(insuranceConventionInsurerValidator);

  const isNewConvention = useMemo(() => !productId, [productId]);

  const { loading: loadingInsuranceConventionInsurers, data: insuranceConventionInsurers } = useQuery(INSURANCE_CONVENTION_INSURERS, {
    skip: productId === undefined || productId === null,
    variables: { productId },
    onCompleted: ({ insuranceConventionInsurers }) => {
      if (insurersLens |> get) return;
      const insurerDetails = insuranceConventionInsurers.map(x => ({
        ...x,
        inEdit: false,
        disabled: false,
        editObject: { ...x }
      }));
      resetInsurersLens(insurerDetails);
    }
  });

  const { data: insurersList } = useQuery(INSURERS);

  const [addOrUpdateInsuranceConventionInsurer] = useMutation(ADD_UPDATE_INSURANCE_CONVENTION_INSURER, {
    onError: ({ params }) => {
      (params.insurer.disabled |> set)(false);
    }
  });

  const [deleteInsuranceConventionInsurer] = useMutation(DELETE_CONVENTION_INSURER, {
    onError: ({ params }) => {
      (params.insurer.disabled |> set)(false);
    }
  });

  const handleAddNewInsurer = useCallback(() => {
    resetInsurersLens(prev => [
      {
        productId: productId,
        insurerId: null,
        isDefault: null,
        editObject: {
          productId: productId,
          insurerId: null,
          isDefault: null
        },
        inEdit: true
      },
      ...prev
    ]);
  }, [productId, resetInsurersLens]);

  const handleSaveDetail = useCallback(
    (insurer, index) => {
      const insuranceConventionXInsurer = get(insurersLens);
      validate(insuranceConventionXInsurer);

      if (!isValid(validation[index])) return;

      const duplicateInsurer = insuranceConventionInsurers?.insuranceConventionInsurers?.find(
        x => x.insurerId === (insurer.editObject.insurerId |> get)
      );
      (insurer.disabled |> set)(true);

      if (duplicateInsurer && duplicateInsurer.insurerId !== (insurer.insurerId |> get)) {
        addToast(t('InsuranceConventions.Details.Insurers.DuplicateInsurerError'), 'error');
        (insurer.disabled |> set)(false);
        return;
      }

      addOrUpdateInsuranceConventionInsurer({
        variables: {
          input: {
            productId: productId,
            insurerId: insurer.editObject.insurerId |> get,
            insurerName: null,
            isDefault: (insurer.editObject.isDefault |> get) || false,
            initialInsurerId: insurer.insurerId |> get
          }
        },
        errorHandlingParams: { insurer }
      });
    },
    [
      addOrUpdateInsuranceConventionInsurer,
      addToast,
      insuranceConventionInsurers?.insuranceConventionInsurers,
      insurersLens,
      productId,
      t,
      validate,
      validation
    ]
  );

  const handleEdit = useCallback(insurer => {
    (insurer.inEdit |> set)(true);
  }, []);

  const handleCancelEditInsurer = useCallback(
    (insurer, index) => {
      (insurer.inEdit |> set)(false);
      if ((insurer.insurerId |> get) === null) {
        resetInsurersLens(prev => {
          prev.splice(index, 1);
          return prev;
        });
      } else {
        resetInsurersLens(prev => {
          return prev.map(x => {
            // eslint-disable-next-line no-unused-vars
            const { editObject, ...rest } = x;
            return { ...rest, editObject: { ...rest } };
          });
        });
      }
    },
    [resetInsurersLens]
  );

  const handleDeleteInsurer = useCallback(
    (productId, insurerId) => {
      confirm(
        () => {
          deleteInsuranceConventionInsurer({ variables: { productId, insurerId }, errorHandlingParams: { productId, insurerId } });
        },
        {
          title: t('InsuranceConventions.Details.Insurers.DeleteInsurer'),
          content: t('InsuranceConventions.Details.Insurers.AreYouSureDeleteInsurer')
        }
      );
    },
    [confirm, deleteInsuranceConventionInsurer, t]
  );

  useSubscription(INSURANCE_CONVENTION_INSURER_UPDATED, {
    onData: ({
      data: {
        data: { insuranceConventionInsurerUpdated }
      },
      client
    }) => {
      resetInsurersLens(prev => {
        const insurers = prev.map(x => {
          if (
            x.editObject.productId === insuranceConventionInsurerUpdated.insuranceConventionInsurer.productId &&
            x.editObject.insurerId === insuranceConventionInsurerUpdated.insuranceConventionInsurer.insurerId
          ) {
            return {
              ...insuranceConventionInsurerUpdated.insuranceConventionInsurer,
              inEdit: false,
              disabled: false,
              editObject: insuranceConventionInsurerUpdated.insuranceConventionInsurer
            };
          } else return x;
        });
        sortBy(insurers, ['insurerName']);
        return insurers;
      });
      addCachedDetail(
        client,
        { name: 'insuranceConventionInsurers', value: INSURANCE_CONVENTION_INSURERS },
        { productId },
        x =>
          x.productId === insuranceConventionInsurerUpdated.insuranceConventionInsurer.productId &&
          x.insurerId === insuranceConventionInsurerUpdated.insuranceConventionInsurer.insurerId,
        insuranceConventionInsurerUpdated.insuranceConventionInsurer,
        ['insurerName']
      );
      addToast(t('InsuranceConventions.Details.Insurers.InsurerUpdated'), 'success');
    }
  });

  useSubscription(CONVENTION_INSURER_DELETED, {
    onData: ({
      data: {
        data: { insuranceConventionInsurerDeleted }
      }
    }) => {
      resetInsurersLens(prev =>
        prev.filter(
          x => !(x.productId === insuranceConventionInsurerDeleted.productId && x.insurerId === insuranceConventionInsurerDeleted.insurerId)
        )
      );
      removeCachedDetail(
        client,
        { name: 'insuranceConventionInsurers', value: INSURANCE_CONVENTION_INSURERS },
        { productId },
        x => x.insurerId === insuranceConventionInsurerDeleted.insurerId,
        ['insurerName']
      );
      addToast(t('InsuranceConventions.Details.Insurers.InsurerDeleted'), 'success');
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <InsuranceConventionInsurerComponent
      insurersLens={insurersLens}
      insurers={insurersList?.insurersList}
      onNewInsurer={handleAddNewInsurer}
      onSaveDetail={handleSaveDetail}
      onEdit={handleEdit}
      onCancelEditInsurer={handleCancelEditInsurer}
      onDeleteInsurer={handleDeleteInsurer}
      productId={productId}
      loadingInsuranceConventionInsurers={loadingInsuranceConventionInsurers}
      isNewConvention={isNewConvention}
      validation={validation}
    />
  );
};

InsuranceConventionInsurerContainer.propTypes = {
  productId: PropTypes.number
};

export default InsuranceConventionInsurerContainer;
